import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Bookmark, Briefcase, CalendarDays, Megaphone, UserRound, Users } from "lucide-react";
import { api } from "@/lib/api";
import { fdate } from "@/lib/format";
import { useAuth, can } from "@/context/AuthContext";
import { Avatar, EmptyState, Loader, StatusBadge } from "@/components/common";
import { Composer, FILTERS, PostCard } from "@/components/member/Feed";
import { Cover } from "@/components/member/ProfileMedia";

function MembershipCard() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/me/membership").then(({ data }) => setM(data)).catch(() => {});
  }, []);
  const current = m?.periods.find((p) => p.status === "active") || m?.periods[0];
  return (
    <Link to="/app/moi?tab=cotisation" data-testid="home-membership-card" className="mr-card flex items-center justify-between gap-4 hover:border-mr-text2 transition-colors">
      <div>
        <p className="text-xs uppercase tracking-wider text-mr-muted">Cotisation annuelle</p>
        <p className="font-medium mt-1 text-sm">{current?.end ? `Jusqu'au ${fdate(current.end)}` : "Aucune période active"}</p>
      </div>
      {current && <StatusBadge status={current.status} testId="home-membership-status" />}
    </Link>
  );
}

function ProfileSummary() {
  const { me } = useAuth();
  const p = me.profile || {};
  return (
    <div className="mr-card p-0 overflow-hidden" data-testid="home-profile-card">
      <Cover fileId={p.cover_file_id} className="h-16" />
      <div className="px-5 pb-5 -mt-8">
        <div className="relative z-10 rounded-full ring-4 ring-mr-surface w-fit"><Avatar name={me.user.name} fileId={me.user.photo_file_id} size={60} /></div>
        <p className="font-display font-semibold mt-3">{me.user.name}</p>
        <p className="text-xs text-mr-text2 mt-0.5">{p.headline || [p.company, p.city].filter(Boolean).join(" · ") || "Complétez votre profil"}</p>
        <div className="mt-4 space-y-1 text-sm">
          <Link to={`/app/reseau/${me.user.id}`} data-testid="home-my-profile-link" className="flex items-center gap-2 py-1.5 text-mr-text2 hover:text-mr-text"><UserRound size={15} /> Mon profil</Link>
          <Link to="/app?filtre=enregistres" data-testid="home-saved-link" className="flex items-center gap-2 py-1.5 text-mr-text2 hover:text-mr-text"><Bookmark size={15} /> Publications enregistrées</Link>
          <Link to="/app/annonces" data-testid="home-announcements-link" className="flex items-center gap-2 py-1.5 text-mr-text2 hover:text-mr-text"><Megaphone size={15} /> Annonces du bureau</Link>
        </div>
      </div>
    </div>
  );
}

function Upcoming() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/commerce/events").then(({ data }) => setItems(data.filter((e) => e.status === "publie" && new Date(e.starts_at) > new Date()).sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)).slice(0, 3))).catch(() => {});
  }, []);
  return (
    <div className="mr-card space-y-3" data-testid="home-events-card">
      <p className="text-xs uppercase tracking-wider text-mr-muted">Événements à venir</p>
      {items.length === 0 ? <p className="text-sm text-mr-text2">Aucun événement programmé.</p> : items.map((e) => (
        <Link key={e.id} to={`/app/evenements/${e.id}`} data-testid="home-event-link" className="flex gap-3 group">
          <CalendarDays size={16} className="text-mr-accent mt-0.5 shrink-0" />
          <span className="min-w-0"><span className="block text-sm font-medium group-hover:text-mr-accent truncate">{e.title}</span><span className="text-xs text-mr-muted tabular">{fdate(e.starts_at)}</span></span>
        </Link>
      ))}
      <Link to="/app/evenements" className="text-xs text-mr-accent inline-flex items-center gap-1">Tous les événements <ArrowRight size={12} /></Link>
    </div>
  );
}

function Discover() {
  const { me } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/members", { params: { limit: 6 } }).then(({ data }) => setItems(data.items.filter((m) => m.id !== me.user.id && !m.is_contact).slice(0, 4))).catch(() => {});
  }, [me.user.id]);
  if (!items.length) return null;
  return (
    <div className="mr-card space-y-3" data-testid="home-discover-card">
      <p className="text-xs uppercase tracking-wider text-mr-muted">Membres à découvrir</p>
      {items.map((m) => (
        <Link key={m.id} to={`/app/reseau/${m.id}`} data-testid="home-discover-member" className="flex items-center gap-3 group">
          <Avatar name={m.name} fileId={m.photo_file_id} size={36} />
          <span className="min-w-0"><span className="block text-sm font-medium truncate group-hover:text-mr-accent">{m.name}</span><span className="block text-xs text-mr-muted truncate">{m.headline || m.company}</span></span>
        </Link>
      ))}
      <Link to="/app/reseau" className="text-xs text-mr-accent inline-flex items-center gap-1">Tout le réseau <ArrowRight size={12} /></Link>
    </div>
  );
}

export function PostPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get(`/feed/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
  }, [id]);
  if (p === null) return <Loader />;
  if (!p) return <EmptyState title="Publication introuvable" />;
  return <ul className="max-w-2xl mx-auto"><PostCard post={p} /></ul>;
}

export default function MemberHome() {
  const { me } = useAuth();
  const [posts, setPosts] = useState(null);
  const [sp, setSp] = useSearchParams();
  const filter = sp.get("filtre") || "tout";
  const setFilter = (v) => setSp(v === "tout" ? {} : { filtre: v });
  const [more, setMore] = useState(false);
  const sentinel = useRef(null);
  const load = (before) => api.get("/feed", { params: { filter, before } }).then(({ data }) => { setMore(data.length >= 20); setPosts((prev) => (before ? [...prev, ...data] : data)); }).catch(() => setPosts([]));
  useEffect(() => {
    if (!more || !sentinel.current) return undefined;
    const io = new IntersectionObserver(([en]) => {
      if (en.isIntersecting) {
        setMore(false);
        load(posts[posts.length - 1].created_at);
      }
    }, { rootMargin: "600px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [more, posts]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setPosts(null);
    load();
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  const prepend = (p) => setPosts((prev) => [p, ...(prev || [])]);
  return (
    <div className="mr-fade-up">
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-6" data-testid="home-greeting">Bonjour {me.user.name.split(" ")[0]}</h1>
      <div className="grid lg:grid-cols-[250px_minmax(0,1fr)_280px] gap-6 items-start">
        <aside className="space-y-4 lg:sticky lg:top-20">
          <div className="hidden lg:block"><ProfileSummary /></div>
          <div className="flex gap-2 lg:hidden">
            <Link to={`/app/reseau/${me.user.id}`} data-testid="home-my-profile-link-mobile" className="mr-btn-secondary flex-1 text-xs"><UserRound size={15} /> Mon profil</Link>
            <button data-testid="home-saved-link-mobile" onClick={() => setFilter("enregistres")} className="mr-btn-secondary flex-1 text-xs"><Bookmark size={15} /> Enregistrés</button>
          </div>
          <MembershipCard />
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
            <Link to="/app/reseau" data-testid="home-shortcut-network" className="mr-card hover:border-mr-text2 transition-colors flex lg:items-center gap-3 flex-col lg:flex-row">
              <Users size={20} className="text-mr-accent" /><span><span className="block font-medium text-sm">Le réseau</span><span className="text-xs text-mr-muted">Trouver un membre</span></span>
            </Link>
            <Link to="/app/operations" data-testid="home-shortcut-operations" className="mr-card hover:border-mr-text2 transition-colors flex lg:items-center gap-3 flex-col lg:flex-row">
              <Briefcase size={20} className="text-mr-accent" /><span><span className="block font-medium text-sm">Opérations</span><span className="text-xs text-mr-muted">Projets, achats, voyages</span></span>
            </Link>
          </div>
          {me.is_staff && <Link to="/console" data-testid="home-console-link" className="mr-btn-secondary w-full">Ouvrir la console <ArrowRight size={16} /></Link>}
        </aside>
        <section className="min-w-0" data-testid="home-feed">
          {can(me, "feed:post") ? <div className="mb-4"><Composer onPosted={prepend} /></div>
            : <p className="text-sm text-mr-warning mb-4" data-testid="feed-readonly-notice">Compte suspendu : lecture seule. Réglez votre cotisation pour publier.</p>}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-3 -mx-4 px-4 lg:mx-0 lg:px-0">{FILTERS.map(([v, l]) => (
            <button key={v} data-testid={`feed-filter-${v}`} onClick={() => setFilter(v)} className={`shrink-0 px-3 h-9 rounded-full border text-xs font-medium transition-colors ${filter === v ? "border-mr-accent text-mr-accent" : "border-mr-border text-mr-text2 hover:border-mr-text2"}`}>{l}</button>))}</div>
          {posts === null ? <Loader /> : posts.length === 0 ? (
            <EmptyState icon={filter === "enregistres" ? Bookmark : Megaphone} title={filter === "enregistres" ? "Aucune publication enregistrée" : "Rien de neuf pour l'instant"}
              text={filter === "enregistres" ? "Touchez « Enregistrer » sous une publication pour la retrouver ici." : "Les annonces du bureau et les publications des membres apparaîtront ici."} />
          ) : (
            <>
              <ul className="space-y-4">{posts.map((p) => <PostCard key={p.id} post={p} onChange={() => load()} onReposted={prepend} />)}</ul>
              {more && <div ref={sentinel} data-testid="feed-load-more" className="py-6"><Loader /></div>}
            </>
          )}
        </section>
        <aside className="hidden lg:block space-y-4 sticky top-20">
          <Upcoming />
          <Discover />
        </aside>
      </div>
    </div>
  );
}
